import { useMemo } from 'react';
import { NothingToShow } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { PanelSkeleton } from '../panelSkeleton/PanelSkeleton';
import { SectionCard } from '../sectionCard/SectionCard';
import { DodStateTone } from '../stateChip/StateChip.types';
import { dodReaderLabel } from '../../overviewTab/dodFormat';

import { TkdHeatmapCell, TkdHeatmapProps } from './TkdHeatmap.types';
import * as Styled from './TkdHeatmap.styles';

/**
 * Whole-fleet view: one tile per TKD, grouped by RCP server.
 *
 * A non-empty dead letter wins over the queue state, because a paused queue with four dead orders
 * is a dead-letter problem first. Each tile spells out its numbers, so the colour is a shortcut,
 * never the only carrier of meaning.
 */
const cellTone = (cell: TkdHeatmapCell): DodStateTone => {
  if (cell.dlqDepth > 0) {
    return 'bad';
  }

  if (cell.status === 'paused') {
    return 'warn';
  }

  if (cell.status === 'draining') {
    return 'info';
  }

  return 'ok';
};

export const TkdHeatmap = ({ title, cells, onSelect, isLoading = false, className }: TkdHeatmapProps): JSX.Element => {
  const { formatMessage } = useLocale();

  const groups = useMemo(() => {
    const byRcp = new Map<string, { name: string; cells: TkdHeatmapCell[] }>();

    cells.forEach((cell) => {
      const group = byRcp.get(cell.rcpId) ?? { name: cell.rcpNazwa ?? cell.rcpId, cells: [] };
      group.cells.push(cell);
      byRcp.set(cell.rcpId, group);
    });

    return Array.from(byRcp.entries()).map(([rcpId, group]) => ({ rcpId, ...group }));
  }, [cells]);

  const legend: Array<{ tone: DodStateTone; message: keyof typeof AppMessages }> = [
    { tone: 'ok', message: 'dod.common.state.idle' },
    { tone: 'info', message: 'dod.common.state.draining' },
    { tone: 'warn', message: 'dod.common.state.paused' },
    { tone: 'bad', message: 'dod.common.heatmap.dlq' },
  ];

  const renderBody = (): JSX.Element => {
    if (isLoading) {
      return <PanelSkeleton height={120} withTitle={false} />;
    }

    if (cells.length === 0) {
      return <NothingToShow text={formatMessage({ id: AppMessages['dod.common.empty'] })} />;
    }

    return (
      <Styled.Rows>
        {groups.map((group) => (
          <Styled.Row key={group.rcpId}>
            <Styled.RcpLabel>{group.name}</Styled.RcpLabel>
            <Styled.Grid>
              {group.cells.map((cell) => (
                <Styled.Cell
                  key={`${cell.rcpId}-${cell.nlk}`}
                  type="button"
                  $tone={cellTone(cell)}
                  // The label ellipsises in a narrow cell, and the NLK sits at its end.
                  title={dodReaderLabel(cell.tkdNazwa, cell.nlk)}
                  data-testid={`dod-heatmap-cell-${cell.rcpId}-${cell.nlk}`}
                  onClick={() => onSelect?.(cell)}
                >
                  <Styled.CellName>{dodReaderLabel(cell.tkdNazwa, cell.nlk)}</Styled.CellName>
                  <Styled.CellValue>
                    {cell.depth}
                    {cell.dlqDepth > 0 &&
                      ` · ${formatMessage({ id: AppMessages['dod.common.heatmap.dlq'] })} ${cell.dlqDepth}`}
                  </Styled.CellValue>
                </Styled.Cell>
              ))}
            </Styled.Grid>
          </Styled.Row>
        ))}
      </Styled.Rows>
    );
  };

  return (
    <SectionCard
      className={className}
      title={title}
      actions={
        <Styled.Legend>
          {legend.map((item) => (
            <Styled.LegendItem key={item.tone}>
              <Styled.LegendSwatch $tone={item.tone} />
              {formatMessage({ id: AppMessages[item.message] })}
            </Styled.LegendItem>
          ))}
        </Styled.Legend>
      }
    >
      {renderBody()}
    </SectionCard>
  );
};
